// src/services/rawMaterialsService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class RawMaterialsService {
  
  // 🧱 Get all raw materials with pagination support
  async getAllRawMaterials(params = {}) {
    try {
      console.log('🧱 Fetching all raw materials with params:', params)
      
      // Use the correct URL from AppURLs
      const response = await ApiService.get('/raw-materials', params)
      console.log('✅ Raw materials fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching raw materials:', error)
      throw error
    }
  }

  // 🧱 Get raw material by ID
  async getRawMaterialById(id) {
    try {
      console.log('🧱 Fetching raw material by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.RAW_MATERIALS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Raw material fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching raw material:', error)
      throw error
    }
  }

  // 🧱 Create new raw material
  async createRawMaterial(rawMaterialData) {
    try {
      console.log('🧱 Creating new raw material with data:', rawMaterialData)
      
      // Validate required fields
      this.validateRawMaterialData(rawMaterialData)
      
      const response = await ApiService.post(AppURLs.RAW_MATERIALS.CREATE, rawMaterialData)
      console.log('✅ Raw material created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating raw material:', error)
      throw error
    }
  }

  // 🧱 Update raw material using PATCH
  async updateRawMaterial(id, rawMaterialData) {
    try {
      console.log('🧱 Updating raw material:', { id, rawMaterialData })
      
      // Validate partial data for updates
      this.validatePartialRawMaterialData(rawMaterialData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.RAW_MATERIALS.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, rawMaterialData)
      console.log('✅ Raw material updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating raw material:', error)
      throw error
    }
  }

  // 🧱 Delete raw material
  async deleteRawMaterial(id) {
    try {
      console.log('🧱 Deleting raw material:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.RAW_MATERIALS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Raw material deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting raw material:', error)
      throw error
    }
  }

  // 🧱 Toggle raw material status
  async toggleRawMaterialStatus(id, isActive) {
    try {
      console.log('🧱 Toggling raw material status:', { id, isActive })
      const endpoint = ApiService.replaceUrlParams(AppURLs.RAW_MATERIALS.UPDATE, { id })
      const response = await ApiService.patch(endpoint, { isActive })
      console.log('✅ Raw material status toggled successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error toggling raw material status:', error)
      throw error
    }
  }

  // 🧱 Search raw materials
  async searchRawMaterials(searchTerm, filters = {}) {
    try {
      console.log('🧱 Searching raw materials:', { searchTerm, filters })
      const params = {
        search: searchTerm,
        ...filters
      }
      const response = await ApiService.get(AppURLs.RAW_MATERIALS.GET_ALL, params)
      console.log('✅ Raw materials search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching raw materials:', error)
      throw error
    }
  }

  // 🧱 Get raw materials with pagination
  async getRawMaterialsPaginated(page = 1, limit = 10, filters = {}) {
    try {
      console.log('🧱 Fetching paginated raw materials:', { page, limit, filters })
      const params = {
        page,
        limit,
        ...filters
      }
      const response = await ApiService.get(AppURLs.RAW_MATERIALS.GET_ALL, params)
      console.log('✅ Paginated raw materials fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching paginated raw materials:', error)
      throw error
    }
  }

  // 🧱 Bulk operations
  async bulkDeleteRawMaterials(materialIds) {
    try {
      console.log('🧱 Bulk deleting raw materials:', materialIds)
      const response = await ApiService.post(AppURLs.RAW_MATERIALS.BULK_DELETE, {
        ids: materialIds
      })
      console.log('✅ Raw materials bulk deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting raw materials:', error)
      throw error
    }
  }

  // 🧱 Bulk status update
  async bulkUpdateStatus(materialIds, isActive) {
    try {
      console.log('🧱 Bulk updating raw material status:', { materialIds, isActive })
      const response = await ApiService.patch(AppURLs.RAW_MATERIALS.BULK_STATUS, {
        ids: materialIds,
        isActive
      })
      console.log('✅ Raw materials bulk status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk updating raw material status:', error)
      throw error
    }
  }

  // 🧱 Get raw material statistics
  async getRawMaterialStats() {
    try {
      console.log('🧱 Fetching raw material statistics')
      const response = await ApiService.get(AppURLs.RAW_MATERIALS.STATS)
      console.log('✅ Raw material statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching raw material statistics:', error)
      throw error
    }
  }

  // 🧱 Validate raw material name/code (check for duplicates)
  async validateRawMaterial(field, value, excludeId = null) {
    try {
      console.log('🧱 Validating raw material:', { field, value, excludeId })
      const params = {
        [field]: value,
        ...(excludeId && { excludeId })
      }
      const response = await ApiService.get(AppURLs.RAW_MATERIALS.VALIDATE, params)
      console.log('✅ Raw material validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating raw material:', error)
      throw error
    }
  }

  // 🧱 Get low stock raw materials
  async getLowStockRawMaterials() {
    try {
      console.log('🧱 Fetching low stock raw materials')
      const response = await ApiService.get(AppURLs.RAW_MATERIALS.LOW_STOCK)
      console.log('✅ Low stock raw materials fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching low stock raw materials:', error)
      throw error
    }
  }

  // 🧱 Get raw materials by unit
  async getRawMaterialsByUnit(unitId) {
    try {
      console.log('🧱 Fetching raw materials by unit:', unitId)
      const params = { unit: unitId }
      const response = await ApiService.get(AppURLs.RAW_MATERIALS.GET_ALL, params)
      console.log('✅ Raw materials by unit fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching raw materials by unit:', error)
      throw error
    }
  }

  // 🛡️ Validation Methods
  validateRawMaterialData(rawMaterialData) {
    const { name, code, unit, threshold } = rawMaterialData

    // Check required fields
    if (!name || !name.trim()) {
      throw new Error('Material name is required')
    }

    if (!code || !code.trim()) {
      throw new Error('Material code is required')
    }

    if (!unit || !unit.trim()) {
      throw new Error('Unit is required')
    }

    if (threshold === undefined || threshold === null || threshold === '') {
      throw new Error('Threshold is required')
    }

    // Validate threshold is a number
    const thresholdNum = Number(threshold)
    if (isNaN(thresholdNum) || thresholdNum < 0) {
      throw new Error('Threshold must be a positive number')
    }

    return true
  }

  // 🛡️ Validate partial data for updates (only validate provided fields)
  validatePartialRawMaterialData(rawMaterialData) {
    const errors = []

    // Only validate fields that are provided
    if (rawMaterialData.name !== undefined) {
      if (!rawMaterialData.name || !rawMaterialData.name.trim()) {
        errors.push('Material name cannot be empty')
      }
    }

    if (rawMaterialData.code !== undefined) {
      if (!rawMaterialData.code || !rawMaterialData.code.trim()) {
        errors.push('Material code cannot be empty')
      }
    }

    if (rawMaterialData.unit !== undefined) {
      if (!rawMaterialData.unit || !rawMaterialData.unit.trim()) {
        errors.push('Unit cannot be empty')
      }
    }

    if (rawMaterialData.threshold !== undefined) {
      const threshold = Number(rawMaterialData.threshold)
      if (isNaN(threshold) || threshold < 0) {
        errors.push('Threshold must be a positive number')
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(', '))
    }

    return true
  }

  // 📝 Search and Filter Methods
  searchRawMaterialsLocal(materials, searchTerm) {
    if (!searchTerm) return materials

    const term = searchTerm.toLowerCase()
    return materials.filter(material => 
      material.name?.toLowerCase().includes(term) ||
      material.code?.toLowerCase().includes(term) ||
      material.description?.toLowerCase().includes(term)
    )
  }

  // Filter materials by status
  filterByStatus(materials, isActive) {
    if (isActive === null || isActive === undefined) return materials
    return materials.filter(material => material.isActive === isActive)
  }

  // Filter materials by unit
  filterByUnit(materials, unitId) {
    if (!unitId) return materials
    return materials.filter(material => material.unit === unitId)
  }

  // Sort materials
  sortRawMaterials(materials, sortBy = 'name', sortOrder = 'asc') {
    return [...materials].sort((a, b) => {
      let valueA = a[sortBy]
      let valueB = b[sortBy]

      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB.toLowerCase()
      }

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }

  // 📊 Analytics Methods
  getRawMaterialsSummary(materials) {
    const summary = {
      total: materials.length,
      active: materials.filter(material => material.isActive).length,
      inactive: materials.filter(material => !material.isActive).length,
      averageThreshold: 0
    }

    // Calculate average threshold
    if (materials.length > 0) {
      const totalThreshold = materials.reduce((sum, m) => sum + (m.threshold || 0), 0)
      summary.averageThreshold = Math.round(totalThreshold / materials.length)
    }

    return summary
  }

  // 🎨 UI Helper Methods
  getStatusColor(isActive) {
    return isActive ? 'success' : 'error'
  }

  getStatusLabel(isActive) {
    return isActive ? 'Active' : 'Inactive'
  }

  getStatusBadgeClasses(isActive) {
    return isActive
      ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'
      : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'
  }

  // Format display name
  formatRawMaterialDisplay(material, unit = null) {
    if (!material) return 'Unknown Material'
    
    const unitText = unit ? ` (${unit.abbreviation})` : ''
    return `${material.name} - ${material.code}${unitText}`
  }

  // Format threshold display
  formatThreshold(threshold, unit = null) {
    const unitText = unit ? ` ${unit.abbreviation}` : ''
    return `${threshold}${unitText}`
  }
}

export default new RawMaterialsService()